<template>
	<div class="button-section mid-container" :v-focus-section="isNativeDevice" data-focus-section="action" tabindex="0">
		<div class="logo-with-title">
			<IconAppLogo />
			<div>
				<h1 class="element-title vl-font-size--80">
					{{ item?.title }}
				</h1>
				<span class="subtitle vl-font-size--22" v-if="episodeInfo !== undefined">{{ episodeInfo }}</span>
			</div>
			<div
				v-if="timeline"
				class="video-timeline-progress"
				:style="{
					background: `linear-gradient(to right, #f13c36 0%, #f13c36 ${timeline}%, #ffffff ${timeline}%, #ffffff 100%)`,
				}"
			></div>
		</div>
		<div class="buttons">
			<button id="play-btn" @click="play" :v-focus="isNativeDevice" ref="playButton">
				<img src="../images/play.png" alt="" />
				<span v-if="timeline" class="vl-font-size--30 color-black">{{ $t("innerpage.continue") }}</span>
				<span v-else class="vl-font-size--30 color-black">{{ $t("innerpage.play") }}</span>
			</button>
			<button
				v-if="isLoggedIn()"
				class="btn--circle add-to-list"
				:class="{ active: addedToList }"
				@click="addItemToList"
				:v-focus="isNativeDevice"
			>
				<IconDefaultAddToList v-if="!addedToList" />
				<IconActiveAddToList v-else />
				<p class="list-btn-action" v-if="addedToList && addActionTriggered">{{ $t("innerpage.added") }}</p>
				<p class="list-btn-action" v-else-if="!addedToList && addActionTriggered">{{ $t("innerpage.removed") }}</p>
			</button>

			<button
				class="btn--circle volume"
				:v-focus="isNativeDevice"
				:class="{ muted: muted === true }"
				@click="triggerVolume"
			>
				<IconVolume />
			</button>
		</div>
	</div>
	<slot />
</template>

<script lang="ts" setup>
	import { computed, onMounted, Ref, ref, watch } from "vue"
	import IconAppLogo from "../icons/IconAppLogo.vue"
	import IconDefaultAddToList from "../icons/IconPlus.vue"
	import IconActiveAddToList from "../icons/IconCheckmark.vue"
	import IconVolume from "../icons/IconVolume.vue"
	import { isNativeDevice } from "../helper"
	import { useUserStore } from "../stores/user"
	import { Movie } from "../types/Movie"
	import { Series } from "../types/Series"
	import { useTimestampStore } from "../stores/timestamps"
	import { useUserProfiles } from "../composables/useUserProfile"
	import { Episode } from "../types/Episode"
	import { isLoggedIn } from "../composables/useAuth"
	import { extractDurationFromKwik } from "../utils/media"
	import api from "../composables/useApi"
	import { useRouter } from "vue-router"
	import { profileList } from "../composables/useListItems"
	import { useMovieStore } from "../stores/movies"
	import { useSeriesStore } from "../stores/series"
	const { addToProfileList, removeFromProfileList } = profileList()
	const { fetchVideoFlagStoreByProfile, storeStartedEntity } = useUserProfiles()
	const moviesStore = useMovieStore()
	const seriesStore = useSeriesStore()
	const userStore = useUserStore()
	const timestampStore = useTimestampStore()
	const router = useRouter()
	const addedToList: Ref<boolean> = ref(false)
	const addActionTriggered = ref(false)
	const timeline = ref(0)
	const duration = ref(0)
	const episodeInfo: Ref<undefined | string> = ref(undefined)
	const retrivedTimestampEpisode = ref(0)
	const entityType: Ref<string> = ref("")
	const nextEpisodeId = ref<string | null>(null)
	import { useI18n } from "vue-i18n"
	const { locale } = useI18n()
	const playButton = ref<HTMLElement | null>(null)
	const emit = defineEmits(["update_season"])

	// Compute start time only after the item is resolved (from prop or fetched)
	const time = ref<number>(0)
	const episodes = ref<Episode[]>([])
	const props = defineProps<{
		item: any
		type: string
		id: number
		episodes?: Episode[]
	}>()

	const muted = computed(() => {
		return userStore.state.mute || false
	})

	onMounted(async () => {
		if (playButton.value && isNativeDevice) {
			playButton.value.focus()
		}
		await itemExists()
		if (props.type === "series") {
			const res = await api.get(
				`/jsonapi/node/episode?jsonapi_include=1&include=field_image&filter[field_series.id]=${props.item.id}`,
				{
					headers: {
						Accept: "application/vnd.api+json",
					},
				}
			)
			episodes.value = res.data.data
		}
	})

	watch(
		() => props.item,
		async (resolvedItem) => {
			if (!resolvedItem) return
			const entityType = ref("")
			const nid = ref(0)
			const parentId = props.type === "series" ? (resolvedItem as Series).id : undefined
			// Prefer unified item reference over props.item
			const entityId = (resolvedItem as Movie)?.id || (resolvedItem as Episode)?.id
			if (resolvedItem?.type === "node--movie") {
				entityType.value = "movie"
				nid.value = (resolvedItem as any)?.drupal_internal__nid
				if (!entityId && !nid.value) return
				if (timestampStore.state.timestamps.length > 0) {
					let timestamp: number | undefined = undefined
					timestampStore.state.timestamps.forEach((element) => {
						if (element.id === nid.value) {
							timestamp = element.timestamp
						}
					})
					time.value = timestamp || 0
				} else {
					const res = await fetchVideoFlagStoreByProfile(entityId, entityType.value, "started", parentId)
					if (res[0]?.timestamp) {
						timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, nid.value)
					}
					time.value = res[0]?.timestamp || 0
				}
				duration.value = extractDurationFromKwik((props.item as any)?.field_kwik_data) ?? 0
			} else if (resolvedItem?.type === "node--series") {
				entityType.value = "episode"
				const res = await fetchVideoFlagStoreByProfile(undefined, entityType.value, undefined, parentId)
				if (res[0]?.video_flag_type === "completed") {
					let episodes: string[] = []
					try {
						const res = await api.get(`/jsonapi/node/episode`, {
							params: {
								"filter[field_series.id]": resolvedItem?.field_series?.id,
								"fields[node--episode]": "drupal_internal__nid",
							},
						})
						episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
					} catch (error) {
						console.error("Failed to fetch episodes:", error)
						return
					}
					const currentId = res[0]?.target_entity.drupal_internal__nid
					const currentIndex = episodes.indexOf(currentId)
					if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
						nextEpisodeId.value = episodes[currentIndex + 1]
						const response = await api.get(
							`${locale.value}/jsonapi/node/episode?jsonapi_include=1&include=field_trailer_file,field_image,field_series,field_series.field_image,field_subtitles,field_subtitles.field_subtitle_file`,
							{
								params: {
									"filter[drupal_internal__nid]": nextEpisodeId.value,
									"filter[langcode]": locale.value,
									"filter[status]": 1,
								},
							}
						)
						retrivedTimestampEpisode.value = response.data.data[0]
						if (response.data.data[0]) {
							episodeInfo.value = `- S:${response.data.data[0].field_season} E:${response.data.data[0].title}`
							emit("update_season", response.data.data[0].field_season)
						}
					}
				} else {
					nid.value = res[0]?.target_entity.drupal_internal__nid

					if (res[0]?.timestamp) {
						timestampStore.addTimestamp(res[0]?.timestamp, "node--episode", nid.value)
					}
					time.value = res[0]?.timestamp || 0
					duration.value = extractDurationFromKwik((res[0]?.target_entity as Episode)?.field_kwik_data) ?? 0
					retrivedTimestampEpisode.value = res[0]?.target_entity
					if (res[0]?.target_entity) {
						episodeInfo.value = `- S:${res[0]?.target_entity?.field_season} E:${res[0]?.target_entity?.title}`
						emit("update_season", res[0]?.target_entity?.field_season)
					}
				}
			}

			timeline.value = (time.value / duration.value) * 100
		},
		{ immediate: true }
	)

	/*
	 * Check if item is already in user's list
	 */
	const itemExists = async () => {
		if (props.type === "series") {
			entityType.value = "series"
		} else {
			entityType.value = "movie"
		}
		await fetchVideoFlagStoreByProfile(props.item.id, entityType.value, "favourite", undefined).then((res) => {
			const exists = res[0]?.id
			if (exists) {
				addedToList.value = true
			} else {
				addedToList.value = false
			}
		})
	}

	/*
	 * Toggle add to my list state
	 */
	const addItemToList = async () => {
		addActionTriggered.value = true
		if (props.type === "series") {
			entityType.value = "series"
		} else {
			entityType.value = "movie"
		}
		const res = await storeStartedEntity("favourite", entityType.value, undefined, props.item.id, undefined)
		if (res === "deleted") {
			addedToList.value = false

			if (props.type === "series") {
				removeFromProfileList(seriesStore.state.profileSeriesList, props.item, 45)
			} else {
				removeFromProfileList(moviesStore.state.profileMoviesList, props.item, 45)
			}
		} else if (res === "created") {
			addedToList.value = true

			if (props.type === "series") {
				addToProfileList(seriesStore.state.profileSeriesList, props.item)
			} else {
				addToProfileList(moviesStore.state.profileMoviesList, props.item)
			}
		}
	}

	const triggerVolume = () => {
		const player = window.kwikMotion(`kwik-player-${props.id}`)

		if (userStore.state.mute === true) {
			player.muted(false)
			userStore.state.mute = false
		} else {
			player.muted(true)
			userStore.state.mute = true
		}
	}

	const play = () => {
		if (props.type === "movie") {
			router.push({ name: "Watch", params: { id: props.item.drupal_internal__nid, type: "movie" } })
		} else {
			if (retrivedTimestampEpisode.value === 0 || retrivedTimestampEpisode.value === undefined) {
				retrivedTimestampEpisode.value = episodes.value[0]?.drupal_internal__nid
			}
			router.push({
				name: "Watch",
				params: {
					id: retrivedTimestampEpisode.value?.drupal_internal__nid
						? retrivedTimestampEpisode.value?.drupal_internal__nid
						: retrivedTimestampEpisode.value,
					type: "episode",
				},
			})
		}
	}
</script>

<style scoped lang="scss">
	// .overlay {
	// display: flex;
	// justify-content: end;
	// flex-direction: column;
	// z-index: 2;
	// height: max-content;
	// margin-top: auto;
	// bottom: 0;
	// right: 0;
	// left: 0;

	.logo-with-title {
		display: flex;
		align-items: end;
		gap: 23px;
		position: relative;
		padding-bottom: 35px;
		.subtitle {
			margin-top: 10px;
			display: inline-block;
		}
		.element-title {
			@extend .vl-font-size--80;
			@include br.respond-to(br.$desktop-medium) {
				font-size: 60px;
				line-height: 1.1;
			}

			@include br.respond-to(br.$tablet-xlarge) {
				font-size: 32px;
				line-height: 44px;
			}
		}

		@include br.tablet-down {
			flex-direction: column;
			align-items: flex-start;
			gap: 15px;
			padding-bottom: 20px;
		}
		.video-timeline-progress {
			max-width: 312px;
			width: 100%;
			position: absolute;
			bottom: 0px;
			height: 1px;
			left: 0;
			right: 0;
			z-index: 2;
			transition: all 0.3s ease;

			@include br.tablet-down {
				max-width: 100%;
			}
		}
	}
	// }
	.buttons {
		display: flex;
		gap: 20px;
		margin-top: 50px;
		align-items: center;

		@include br.respond-to(br.$tablet-medium) {
			margin-top: 25px;
		}
	}
	#play-btn {
		background: var(--white);
		height: 66px;
		border: 0px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		gap: 20px;
		padding: 20px 15px;
		transition: all 0.3s ease;

		@include br.respond-to(br.$tablet-medium) {
			height: 50px;
			padding: 15px 10px;
			gap: 10px;

			img {
				max-width: 30px;
			}
			span {
				font-size: 18px;
			}
		}

		&:hover {
			border-radius: 20px;
			cursor: pointer;
		}
	}
	.volume,
	.add-to-list {
		position: relative;
		width: 45px;
		height: 45px;
		svg {
			width: 25px;
			height: 25px;
		}
		.active {
			display: none;
		}
		.default {
			display: block;
		}
		&.active {
			.active {
				display: block;
			}
			.default {
				display: none;
			}
		}
	}
	.list-btn-action {
		position: absolute;
		right: -100px;
		animation: hideAnimation 0s 1.5s forwards;
	}
	.volume {
		margin-left: auto;
		&.muted {
			position: relative;
			opacity: 0.5;
			&::after {
				content: "";
				position: absolute;
				display: flex;
				left: 5px;
				right: 5px;
				height: 3px;
				background: var(--white);
				transform: rotate(45deg);
			}
			&:hover {
				opacity: 1;
			}
		}
		&:hover {
			cursor: pointer;
		}
	}
	@keyframes hideAnimation {
		to {
			visibility: hidden;
		}
	}
</style>
